import CloseIcon from '@material-ui/icons/Close';
import { Dialog } from '@mui/material';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';

import AttachmentUpload from '../AttachmentUpload';
import { FooterButton } from '../FooterButtons/styled';
import { 
    AttachmentUploadWrapper, BlockerDialogButtonContainer,
    BlockerDialogCloseButton, 
    BlockerDialogContent, 
    BlockerDialogDescription,
    BlockerTextField,
    dialogPaperProps, 
    DialogTitle } from './styled';

interface BlockerDialogProps {
    open: boolean;
    onClose: () => void;
    blockerDescription: string;
    onBlockerDescriptionChange: (description: string) => void;
    onSubmit: (attachmentIds: number[]) => void;
}

const BlockerDialog: FunctionComponent<BlockerDialogProps> = ({
    open,
    onClose,
    blockerDescription,
    onBlockerDescriptionChange,
    onSubmit
}) => {
    const [ attachmentIds, setAttachmentIds ] = useState<number[]>([]);
    const [ isUploading, setIsUploading ] = useState(false);

    const handleSubmit = useCallback(() => {
        onSubmit(attachmentIds);
    }, [ attachmentIds, onSubmit ]);

    // Reset attachmentIds when dialog closes
    useEffect(() => {
        if (!open) {
            setAttachmentIds([]);
        }
    }, [ open ]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="blocker-dialog-title"
            aria-describedby="blocker-dialog-description"
            PaperProps={dialogPaperProps}
            maxWidth="md"
            fullWidth
        >
            <BlockerDialogCloseButton onClick={onClose}>
                <CloseIcon />
            </BlockerDialogCloseButton>
            <DialogTitle id="blocker-dialog-title">Blocker Description</DialogTitle>
            <BlockerDialogContent>
                <BlockerDialogDescription>
                    The Report Blocker feature should be used when a developer cannot proceed due to unclear or incorrect requirements, or missing knowledge. First, use the Discussion feature to seek clarification or assistance. If the issue remains unresolved, report it as a blocker.
                    <br />
                    Note: This feature is not intended for bugs. Bugs should be reported through our extension to receive direct technical support.
                </BlockerDialogDescription>
                <BlockerTextField
                    multiline
                    rows={6}
                    variant="outlined"
                    fullWidth
                    placeholder="Please describe the blocker..."
                    value={blockerDescription}
                    onChange={(e) => onBlockerDescriptionChange(e.target.value)}
                />
                <AttachmentUploadWrapper>
                    <AttachmentUpload
                        attachmentIds={attachmentIds}
                        setAttachmentIds={setAttachmentIds}
                        hideHeader
                        persistToStorage={false}
                        enablePaste={false}
                        showOnlyCompleted
                        onUploadingChange={setIsUploading}
                    />
                </AttachmentUploadWrapper>
                <BlockerDialogButtonContainer>
                    <FooterButton onClick={onClose}>
                        Cancel
                    </FooterButton>
                    <FooterButton 
                        onClick={handleSubmit}
                        disabled={!blockerDescription.trim() || isUploading}
                    >
                        Submit
                    </FooterButton>
                </BlockerDialogButtonContainer>
            </BlockerDialogContent>
        </Dialog>
    );
};

export default BlockerDialog;
